'use client';

import { useTranslation } from 'next-i18next';
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { DecorativeGroup, Button, Input } from '@components/common/Forms';
import {
  FormEvent, useEffect, useRef, useState,
} from 'react';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';

/**
 * @description Renders the company job search section in the navbar.
 */

export const SearchMyJobSection = () => {
  const { t } = useTranslation('common');

  const { searchParams } = useExtendedSearchParams();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [keyword, setKeyword] = useState<string>('');

  // Handle the form submit and navigate to the search page if needed
  // ***********************************************
  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const keywordInput = formRef.current?.querySelector<HTMLInputElement>(
      'input[name="keyword"]',
    );

    const queryParams = new URLSearchParams(searchParams || {});

    // Update queryParams based on keyword value
    if (keyword) {
      queryParams.set('keyword', keyword);
      window.history.pushState(null, '', `?${queryParams.toString()}`);
    } else {
      // Remove keyword from queryParams if empty
      queryParams.delete('keyword');
      window.history.pushState(null, '', `?${queryParams.toString()}`);
    }

    setIsFocused(false);
    keywordInput?.blur();
  };

  const triggerInputFocus = () => {
    setIsFocused(true);
    // Ensure the formRef exists
    if (formRef.current) {
      // Use querySelector to safely access the input within the form
      const input = formRef.current.querySelector<HTMLInputElement>(
        'input[name="keyword"]',
      );

      // Check if the input exists and focus on it
      if (input) {
        input.focus();
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams || {});
    const keywordParam = params.get('keyword');

    // If there is a keyword query param set state accordingly
    if (keywordParam) {
      setKeyword(keywordParam);
    }
  }, [searchParams]);

  // Render component
  // *****************************************************
  return (
    <>
      <div className="relative z-1 flex w-full items-center md:absolute md:left-10 md:w-auto">
        <form
          ref={formRef}
          onSubmit={handleSearchSubmit}
          onFocus={() => setIsFocused(true)}
          className={`${
            isFocused ? 'md:w-92' : 'md:w-52'
          } relative w-full !rounded-md bg-surface-100 transition-all duration-300`}
        >
          <DecorativeGroup groupParentClasses="w-full" className="border-0">
            <Button
              styling="none"
              onClick={triggerInputFocus}
              className="flex shrink-0 items-center pl-3 outline-none"
            >
              <MagnifyingGlassIcon className="size-5 stroke-2" />
            </Button>

            <Input
              type="text"
              name="keyword"
              placeholder={t('header.autocomplete.placeholder.my-jobs')}
              className="rounded-md border-0 bg-transparent text-base"
              onBlur={() => {
                setIsFocused(false);
              }}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
              }}
              onFocus={() => setIsFocused(true)}
            />
            {/* Submit button; tabindex for Safari */}
            <div className="relative z-10 flex shrink-0 items-center pr-1.5">
              <Button
                type="submit"
                size="sm"
                rounding="full"
                color="secondary"
                name="keywordSubmit"
                tabIndex={-1}
                className={`${
                  isFocused ? 'size-9 scale-100' : 'size-0 scale-0'
                } items-center justify-center !p-0 transition-all delay-300 duration-300`}
              >
                <ArrowRightIcon className="mr-px size-4 stroke-[3px]" />
              </Button>
            </div>
          </DecorativeGroup>
        </form>
      </div>
    </>
  );
};
